import { newAuth } from '@/firebase/firebaseInit';
import i18n from '@/i18n';
import { isRateLimitedBody, notifyUsageLimit, usageLimitMessage } from '@/utils/usageLimitNotice';

const AI_API_URL: string = import.meta.env.VITE_AI_API_URL || 'http://localhost:8085';

// --- Types ---

export interface ChatMessage {
  role: 'user' | 'ai';
  text: string;
  id: string | null;
  feedbackGiven: boolean;
  feedbackRating: FeedbackRating | null;
}

export type FeedbackRating = 'bad' | 'fine' | 'good';

export interface FeedbackPayload {
  messageId: string | null;
  question: string;
  answer: string;
  rating: FeedbackRating;
  comment: string;
}

export interface UserContext {
  partnerTypes?: string[];
  roles?: string[];
  userGroup?: string;
  email?: string;
}

export interface ConversationMessage {
  role: 'user' | 'ai';
  text: string;
}

// --- Helpers ---

async function getAuthToken(): Promise<string> {
  const user = (newAuth as any).currentUser;
  if (user) {
    return await user.getIdToken(false);
  }
  return '';
}

/**
 * The UI language as a two-letter code, sent with every question so the assistant can render
 * language-specific CS training items (ticket 2026-10-21-info-email-parser, round 4 review F-8).
 */
function uiLanguage(): string {
  const raw = (i18n.global.locale as { value?: unknown }).value ?? i18n.global.locale;
  const code = String(raw ?? '').slice(0, 2).toLowerCase();
  return code || 'en';
}

function buildHeaders(token: string): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// --- Service ---

class AiChatService {
  /**
   * Fetch chat history (last 7 days, max 50 messages)
   */
  async fetchHistory(): Promise<ChatMessage[]> {
    const token = await getAuthToken();
    const response = await fetch(`${AI_API_URL}/v1/history`, {
      headers: buildHeaders(token),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch history: ${response.status}`);
    }

    const data = await response.json();
    return (data.history || []).map((msg: any) => ({
      role: msg.role,
      text: msg.text,
      id: msg.id || null,
      feedbackGiven: false,
      feedbackRating: null,
    }));
  }

  /**
   * Send a question to the AI and stream the response via SSE.
   *
   * @param question   - The user's question
   * @param userContext - User metadata (partnerTypes, roles, userGroup, email)
   * @param onChunk    - Callback for each streamed text chunk
   * @param onError    - Callback for errors
   * @returns The full accumulated answer text
   */
  async askQuestion(
    question: string,
    userContext: UserContext,
    conversationContext: ConversationMessage[],
    onChunk: (text: string) => void,
    onError: (errorMsg: string) => void,
  ): Promise<string> {
    const token = await getAuthToken();
    const env: string = import.meta.env.VITE_ENVIRONMENT || 'development';

    const response = await fetch(`${AI_API_URL}/v1/ask`, {
      method: 'POST',
      headers: buildHeaders(token),
      body: JSON.stringify({
        corpus: `ryd-portal-docs-${env}`,
        question,
        language: uiLanguage(),
        userContext,
        conversationContext,
      }),
    });

    if (!response.ok) {
      // An enforced usage limit: say so plainly instead of a generic error.
      if (response.status === 429) {
        const body = await response.json().catch(() => null);
        if (isRateLimitedBody(body)) {
          notifyUsageLimit(body);
          const limitError = new Error(usageLimitMessage(body));
          limitError.name = 'UsageLimitError';
          throw limitError;
        }
      }
      throw new Error(`API Error: ${response.status}`);
    }

    const reader = response.body!.getReader();
    const decoder = new TextDecoder('utf-8');
    let done = false;
    let buffer = '';
    let fullAnswer = '';

    while (!done) {
      const { value, done: doneReading } = await reader.read();
      done = doneReading;
      if (value) {
        buffer += decoder.decode(value, { stream: true });

        let boundary = buffer.indexOf('\n\n');
        while (boundary !== -1) {
          const event = buffer.substring(0, boundary);
          buffer = buffer.substring(boundary + 2);

          if (event.startsWith('data: ')) {
            const dataStr = event.substring(6);
            if (dataStr === '[DONE]') {
              done = true;
              break;
            }
            try {
              const dataObj = JSON.parse(dataStr);
              if (dataObj.text) {
                fullAnswer += dataObj.text;
                onChunk(dataObj.text);
              } else if (dataObj.error) {
                onError(`[Backend Error: ${dataObj.error}]`);
              }
            } catch (e) {
              console.error('Failed to parse SSE data', e, dataStr);
              onError(`[Parse Error: ${dataStr}]`);
            }
          }
          boundary = buffer.indexOf('\n\n');
        }
      }
    }

    // Flush remaining buffer
    if (buffer.trim().startsWith('data: ')) {
      const dataStr = buffer.trim().substring(6);
      if (dataStr !== '[DONE]') {
        try {
          const dataObj = JSON.parse(dataStr);
          if (dataObj.text) {
            fullAnswer += dataObj.text;
            onChunk(dataObj.text);
          }
        } catch (e) {
          onError(`[Flush Parse Error: ${dataStr}]`);
        }
      }
    }

    return fullAnswer;
  }

  /**
   * Submit feedback for an AI answer.
   */
  async submitFeedback(payload: FeedbackPayload): Promise<{ status: string }> {
    const token = await getAuthToken();

    const response = await fetch(`${AI_API_URL}/v1/feedback`, {
      method: 'POST',
      headers: buildHeaders(token),
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Failed to submit feedback: ${response.status}`);
    }

    return await response.json();
  }
}

export const aiChatService = new AiChatService();
export default aiChatService;
